import React, { useEffect, useRef, useState } from 'react';
import { api } from '../api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Send, Loader2, Trash2, MessageCircle, ShieldCheck, GraduationCap, Crown } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../AuthContext';

const ROLE_BADGE = {
  ADMIN: { label: 'Admin', icon: Crown, cls: 'bg-[#C8102E] text-white border-[#C8102E]' },
  TRAINER: { label: 'Trainer', icon: GraduationCap, cls: 'bg-[#1B3A87] text-white border-[#1B3A87]' },
  FRONT_DESK: { label: 'Front Desk', icon: ShieldCheck, cls: 'bg-[#B08D57] text-white border-[#B08D57]' },
  CLIENT: { label: 'Member', icon: null, cls: 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-200 dark:border-slate-700' },
};

const formatTime = (iso) => {
  try {
    const d = new Date(iso);
    const today = new Date();
    const isToday = d.toDateString() === today.toDateString();
    return isToday ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
                   : d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
};

const initials = (name = '') => name.split(' ').filter(Boolean).slice(0, 2).map((s) => s[0]?.toUpperCase()).join('') || 'U';

/**
 * Reusable Chat Panel (used for universal + team chat).
 *
 * Props:
 *  - channel: "universal" | "team"
 *  - title: string
 *  - subtitle: string
 *  - canPost: boolean
 *  - readOnlyHint: string shown when user cannot post
 *  - pollMs: polling interval
 *  - height: tailwind height (default md:h-[520px])
 */
export const ChatPanel = ({
  channel,
  title,
  subtitle,
  canPost = true,
  readOnlyHint = 'Sign in to participate.',
  pollMs = 5000,
  height = 'h-[460px] md:h-[520px]',
}) => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/chat/${channel}`, { params: { limit: 100 } });
      setMessages(data);
    } catch (err) {
      if (err.response?.status !== 401 && err.response?.status !== 403) {
        // network or 500 — silent for chat UX
      }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!user && channel === 'team') return; // team requires staff
    load();
    const id = setInterval(load, pollMs);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, pollMs, user?.id]);

  useEffect(() => {
    if (!scrollRef.current) return;
    scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault?.();
    const body = text.trim();
    if (!body) return;
    setSending(true);
    try {
      const { data } = await api.post(`/chat/${channel}`, { body });
      setMessages((m) => [...m, data]);
      setText('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send');
    } finally { setSending(false); }
  };

  const del = async (m) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await api.delete(`/chat/${channel}/${m.id}`);
      setMessages((arr) => arr.filter((x) => x.id !== m.id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <Card
      className='border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-hidden flex flex-col'
      data-testid={`chat-panel-${channel}`}
    >
      <div className='px-4 sm:px-5 py-3 border-b border-slate-200 dark:border-slate-800 flex items-center gap-3 bg-gradient-to-r from-[#1B3A87] to-[#16306F] text-white'>
        <div className='w-9 h-9 rounded-md bg-white/15 flex items-center justify-center'>
          <MessageCircle className='w-4 h-4' />
        </div>
        <div className='flex-1 min-w-0'>
          <div className='font-display font-bold text-sm sm:text-base truncate'>{title}</div>
          <div className='text-[11px] text-white/80 truncate'>{subtitle}</div>
        </div>
        <Badge className='bg-white/15 hover:bg-white/20 text-white border-0 text-[10px] tracking-wider font-semibold uppercase'>
          {channel === 'team' ? 'Staff Only' : 'Universal'}
        </Badge>
      </div>

      <div
        ref={scrollRef}
        className={`flex-1 overflow-y-auto px-3 sm:px-4 py-3 space-y-3 ${height} bg-slate-50 dark:bg-slate-950`}
        data-testid={`chat-messages-${channel}`}
      >
        {loading ? (
          <div className='flex items-center justify-center h-32 text-slate-500'>
            <Loader2 className='w-4 h-4 animate-spin mr-2' /> Loading messages…
          </div>
        ) : messages.length === 0 ? (
          <div className='text-center py-10 text-slate-500 dark:text-slate-400 text-sm'>
            No messages yet. Be the first to say hello.
          </div>
        ) : (
          messages.map((m) => {
            const meta = ROLE_BADGE[m.author_role] || ROLE_BADGE.CLIENT;
            const Icon = meta.icon;
            const mine = user?.id === m.author_id;
            const canDelete = mine || user?.role === 'ADMIN';
            return (
              <div
                key={m.id}
                className='flex items-start gap-3 group'
                data-testid={`chat-message-${m.id}`}
              >
                <Avatar className='w-9 h-9 shrink-0 border border-slate-200 dark:border-slate-700'>
                  {m.author_avatar && <AvatarImage src={m.author_avatar} alt={m.author_name} />}
                  <AvatarFallback className='text-xs font-semibold bg-[#1B3A87] text-white'>
                    {initials(m.author_name)}
                  </AvatarFallback>
                </Avatar>
                <div className='flex-1 min-w-0'>
                  <div className='flex items-center gap-2 flex-wrap'>
                    <span className='text-sm font-semibold text-slate-900 dark:text-slate-100 truncate'>
                      {m.author_name}
                    </span>
                    <span
                      className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-bold border ${meta.cls}`}
                      data-testid={`role-badge-${m.author_role}`}
                    >
                      {Icon && <Icon className='w-2.5 h-2.5' />} {meta.label}
                    </span>
                    <span className='text-[11px] text-slate-500 dark:text-slate-400'>{formatTime(m.created_at)}</span>
                    {canDelete && (
                      <button
                        onClick={() => del(m)}
                        className='ml-auto opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-[#B42318] p-1'
                        title='Delete message'
                        data-testid={`chat-delete-${m.id}`}
                      >
                        <Trash2 className='w-3.5 h-3.5' />
                      </button>
                    )}
                  </div>
                  <div className='mt-0.5 text-sm text-slate-800 dark:text-slate-200 leading-relaxed whitespace-pre-wrap break-words'>
                    {m.body}
                  </div>
                  {m.image_base64 && (
                    <img
                      src={m.image_base64}
                      alt=''
                      className='mt-2 max-h-64 rounded-md border border-slate-200 dark:border-slate-700'
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>

      <form
        onSubmit={send}
        className='border-t border-slate-200 dark:border-slate-800 p-3 flex items-center gap-2 bg-white dark:bg-slate-900'
      >
        {canPost ? (
          <>
            <Input
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={channel === 'team' ? 'Message the staff team…' : 'Message the JFK9 pack…'}
              className='h-10 bg-white dark:bg-slate-800 dark:border-slate-700 dark:text-slate-100'
              data-testid={`chat-input-${channel}`}
              maxLength={4000}
            />
            <Button
              type='submit'
              disabled={!text.trim() || sending}
              className='bg-[#1B3A87] hover:bg-[#16306F] text-white h-10 shrink-0'
              data-testid={`chat-send-${channel}`}
            >
              {sending ? <Loader2 className='w-4 h-4 animate-spin' /> : <Send className='w-4 h-4' />}
            </Button>
          </>
        ) : (
          <div className='flex-1 text-xs text-slate-500 dark:text-slate-400 text-center py-2'>
            {readOnlyHint}
          </div>
        )}
      </form>
    </Card>
  );
};

export default ChatPanel;
